<!-- eslint-disable rulesdir/no-retired-components -->
<template>
  <div class="komodo-datepicker">
    <vc-date-picker
      ref="vcDatePickerRef"
      :value="datePickerDates"
      :is-range="isRange"
      :locale="locale"
      :popover="popover"
      :theme="theme"
      :mode="mode"
      :max-date="maxDate"
      :min-date="minDate"
      @input="setDatePickerValue"
    >
      <template #default="{ inputValue, inputEvents }">
        <komodo-input
          :value="inputValue"
          :disabled="disabled"
          :errored="errored"
          :errors="errors"
          :placeholder="placeholder"
          :title="title"
          :help="help"
          :readonly="isRange"
          class="komodo-datepicker-input"
          v-on="{
            input: (event) => inputEventsInput(inputEvents, event),
            click: (event) => inputEventsClick(inputEvents, event)
          }"
        >
          <template #left>
            <komodo-icon
              :icon="icon"
              class="komodo-datepicker-icon"
            />
          </template>
        </komodo-input>
      </template>
    </vc-date-picker>
  </div>
</template>

<script lang="ts">
/* eslint-disable rulesdir/no-retired-components */
import { PropType, computed, watch, ref, Ref, defineComponent } from 'vue'
// eslint-disable-next-line import/no-named-default
import { default as VcDatePicker } from 'v-calendar/lib/components/date-picker.umd'
import KomodoIcon from 'shared/components/KomodoIcon.vue'
import KomodoInput from 'shared/components/KomodoInput.vue'

import { startOfDay, endOfDay } from 'date-fns'

export enum Mode {
  Single = 'single',
  Range = 'range'
}

export interface DateRange {
  start: Date
  end: Date
}

export type DatepickerValue = Date | DateRange | null

const ICONS = {
  [Mode.Single]: 'today',
  [Mode.Range]: 'date_range'
}

const PLACEHOLDERS = {
  [Mode.Single]: '_ _ / _ _ / _ _ _ _',
  [Mode.Range]: '_ _ / _ _ / _ _ _ _ - _ _ / _ _ / _ _ _ _'
}

const locale = {
  masks: {
    title: 'MMMM YYYY',
    weekdays: 'WW'
  }
}

const theme = {
  arrows: {
    light: 'komodo-datepicker-arrows-light'
  },
  container: {
    light: 'komodo-datepicker-container-light'
  },
  dayContent: 'komodo-datepicker-day-content',
  highlightStartEndClass: 'komodo-datepicker-highlight-start-end',
  title: {
    light: 'komodo-datepicker-title-light'
  },
  weekdays: {
    light: 'komodo-datepicker-weekdays-light'
  },
  dayContentDisabled: 'komodo-datepicker-day-content-disabled'
}

export default defineComponent({
  name: 'KomodoDatepicker',
  components: {
    KomodoIcon,
    KomodoInput,
    VcDatePicker
  },
  props: {
    value: {
      type: [Date, Object] as PropType<DatepickerValue>,
      default: null
    },
    mode: {
      type: String as PropType<Mode>,
      default: Mode.Single
    },
    title: {
      type: String,
      default: ''
    },
    help: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    maxDate: {
      type: Date,
      default: undefined
    },
    minDate: {
      type: Date,
      default: undefined
    }
  },
  emits: {
    input: (value: DatepickerValue) => value
  },
  setup (props, { emit }) {
    const vcDatePickerRef = ref<InstanceType<typeof VcDatePicker> | null >(null)
    const datePickerDates: Ref<DatepickerValue | null> = ref(props.value)
    // this is to accomodate someone hard-setting the value field
    // outside of picking the date
    watch(() => props.value, (value) => {
      if (datePickerDates.value !== value) datePickerDates.value = value
    })

    const popover = computed(() => {
      return {
        placement: 'bottom-start',
        visibility: props.disabled ? 'hidden' : 'click',
        modifiers: {
          name: 'flip', enabled: false
        }
      }
    })

    const icon = computed((): string => {
      return ICONS[props.mode]
    })

    const placeholder = computed((): string => {
      return PLACEHOLDERS[props.mode]
    })

    const errored = computed((): boolean => {
      return props.errors.length > 0
    })

    const isRange = computed((): boolean => {
      return props.mode === Mode.Range
    })

    const setDatePickerValue = (vDatePickerValue: DatepickerValue) => {
      if (isRangeMode(vDatePickerValue)) {
        datePickerDates.value = {
          end: endOfDay(vDatePickerValue?.end),
          start: startOfDay(vDatePickerValue?.start)
        }
      } else {
        datePickerDates.value = vDatePickerValue
      }

      emit('input', datePickerDates.value)
      vcDatePickerRef.value?.hidePopover()
    }

    const isRangeMode = (value: DatepickerValue): value is DateRange => {
      return value !== null && Mode.Range === props.mode
    }

    const inputEventsClick = (inputEvents, event) => {
      if (inputEvents.start && inputEvents.start.click) {
        inputEvents.start.click(event)
      }
      if (inputEvents.click) {
        inputEvents.click(event)
      }
    }

    const inputEventsInput = (inputEvents, event) => {
      if (inputEvents.input) {
        inputEvents.input({ target: { value: event } })
      }
    }

    return {
      vcDatePickerRef,
      datePickerDates,
      errored,
      icon,
      isRange,
      placeholder,
      popover,
      setDatePickerValue,
      inputEventsClick,
      inputEventsInput,
      locale,
      theme
    }
  }
})
</script>

<style lang="scss" scoped>
.komodo-datepicker-input ::v-deep input {
  padding-left: 52px;
}

.komodo-datepicker-icon {
  color: $gray-4-24;
}

.komodo-datepicker {
  position: relative;
}

.komodo-datepicker ::v-deep {
  .vc-day.in-next-month,
  .vc-day.in-prev-month {
    display: none;
  }

  .vc-highlight-base-start,
  .vc-highlight-base-middle,
  .vc-highlight-base-end {
    @include background-gray-5-08;

    height: 32px;
  }

  .komodo-datepicker-day-content:not(.komodo-datepicker-day-content-disabled) {
    color: $gray-black;

    &.vc-font-bold {
      font-weight: normal;
    }

    &.vc-text-white {
      color: $gray-white;
    }

    &:focus,
    &:hover {
      @include background-action-blue-base;

      border-radius: 50%;
      cursor: pointer;
      color: $gray-white;
    }
  }

  .komodo-datepicker-day-content-disabled {
    color: $gray-4-24;
    pointer-events: none
  }

  .komodo-datepicker-day-content {
    height: 32px;
    margin: 4px;
    width: 32px;
  }

  .komodo-datepicker-highlight-start-end {
    @include background-action-blue-base;

    border-radius: 50%;
    border-width: 0;
    height: 32px;
    width: 32px;
  }

  .komodo-datepicker-title-light {
    @include font-heading-header-6;
  }

  .komodo-datepicker-weekdays-light {
    @include color-gray-3-60;

    font-size: 14px;
    font-weight: 500;
  }

  .komodo-datepicker-arrows-light {
    @include color-action-blue-base;

    height: 22px;
    width: 22px;
  }

  .vc-arrows-container,
  .vc-weeks,
  .vc-header {
    padding: 16px;
  }

  .vc-header {
    padding-bottom: 0;
  }

  .vc-popover-caret {
    display: none;
  }

  .komodo-datepicker-container-light {
    @include font-paragraph-p1-baseline;

    background: $gray-white;
    border-radius: 4px;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.20),
      0 4px  5px rgba(0, 0, 0, 0.12),
      0 2px  4px rgba(0, 0, 0, 0.14);
    margin-top: 0;
  }
}
</style>
